import React from 'react';
import { Loader2Icon } from 'lucide-react';
import { useInquiryForm } from '../../hooks/useInquiryForm';
import { buttonClasses } from '../../utils/buttonStyles';
import { compactErrors, emailError, requiredChoice, requiredText } from '../../utils/validation';
import { ConsentField } from './ConsentField';
import { FormField } from './FormField';
import { FormStatusNotice } from './FormStatusNotice';
import { FormSuccess } from './FormSuccess';
import { HoneypotField } from './HoneypotField';
type GeneralValues = {
  name: string;
  email: string;
  organization: string;
  inquiryType: string;
  message: string;
  privacy: boolean;
};
const FORM_ID = 'general';
const initialValues: GeneralValues = {
  name: '',
  email: '',
  organization: '',
  inquiryType: '',
  message: '',
  privacy: false
};
const inquiryOptions = ['Employer / hiring inquiry', 'Candidate introduction', 'Partnership or media', 'Other'].map(v => ({
  value: v,
  label: v
}));
export function GeneralInquiryForm() {
  const form = useInquiryForm<GeneralValues>({
    formId: FORM_ID,
    type: 'general',
    initialValues,
    mailSubject: 'General inquiry — Aurevane Search Partners',
    validate: v => compactErrors({
      name: requiredText(v.name, 'your name', 2),
      email: emailError(v.email),
      inquiryType: requiredChoice(v.inquiryType, 'an inquiry type'),
      message: requiredText(v.message, 'a message', 15),
      privacy: v.privacy ? undefined : 'Please confirm you have read the Privacy Policy.'
    }),
    summarize: v => [`Name: ${v.name}`, `Email: ${v.email}`, `Organization: ${v.organization || '—'}`, `Inquiry type: ${v.inquiryType}`, '', v.message]
  });
  const {
    values,
    errors,
    status,
    setField
  } = form;
  const id = (key: keyof GeneralValues) => `${FORM_ID}-${key}`;
  if (status === 'success') {
    return <FormSuccess title="Thank you — your message has been received." text="A member of our team will reply to the email address you provided." resetLabel="Send another message" onReset={form.reset} data-id="element-173" data-mp="w2rqhb6tfhzc" />;
  }
  return <form noValidate onSubmit={form.handleSubmit} className="relative space-y-6" data-id="element-174" data-mp="1ppw6nh3nk8yr">
      <HoneypotField id={`${FORM_ID}-website`} value={form.honeypot} onChange={form.setHoneypot} data-id="element-175" data-mp="1uj8sxkkv03oc" />
      <div className="grid gap-6 md:grid-cols-2" data-id="element-176" data-mp="1zhab83agnyyr">
        <FormField id={id('name')} label="Name" required autoComplete="name" value={values.name} onChange={v => setField('name', v)} error={errors.name} data-id="element-177" data-mp="12s1dd1squs1e" />
        <FormField id={id('email')} label="Email" type="email" required autoComplete="email" value={values.email} onChange={v => setField('email', v)} error={errors.email} data-id="element-178" data-mp="16teh96ca0l5o" />
        <FormField id={id('organization')} label="Organization" autoComplete="organization" value={values.organization} onChange={v => setField('organization', v)} data-id="element-179" data-mp="1gs3qkaq3wxab" />
        <FormField id={id('inquiryType')} label="Inquiry Type" as="select" required options={inquiryOptions} value={values.inquiryType} onChange={v => setField('inquiryType', v)} error={errors.inquiryType} data-id="element-180" data-mp="2s5y3iitmnolt" />
        <FormField id={id('message')} className="md:col-span-2" label="Message" as="textarea" required rows={6} maxLength={3000} value={values.message} onChange={v => setField('message', v)} error={errors.message} data-id="element-181" data-mp="bjrgzpwx8tnm" />
      </div>
      <ConsentField id={id('privacy')} checked={values.privacy} onChange={v => setField('privacy', v)} error={errors.privacy} purpose="to respond to my message" data-id="element-182" data-mp="1toodqtpphpt" />
      <FormStatusNotice status={status} message={form.message} mailtoHref={form.mailtoHref} data-id="element-183" data-mp="3oyo2xqb5add6" />
      <button type="submit" disabled={status === 'submitting'} className={buttonClasses('primary', 'lg')} data-id="element-184" data-mp="3jzb5ycqk7pwv">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-185" data-mp="3f2uf3arz6xg3" />
            Sending…
          </> : 'Send Message'}
      </button>
    </form>;
}